import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { 
  Trophy, 
  Sparkles, 
  Target, 
  Flame,
  Star,
  PartyPopper,
  TrendingUp,
  CheckCircle2,
  Volume2,
  VolumeX
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const motivationalMessages = [
  { title: "You're Crushing It!", subtitle: "Another task conquered. Keep up the momentum!" },
  { title: "Fantastic Work!", subtitle: "Your dedication is truly inspiring." },
  { title: "Goal Achieved!", subtitle: "One step closer to greatness." },
  { title: "Unstoppable!", subtitle: "Nothing can slow you down today." },
  { title: "Champion Move!", subtitle: "Winners finish what they start." },
  { title: "Brilliant!", subtitle: "Your productivity is off the charts." },
  { title: "Nailed It!", subtitle: "Excellence is your new standard." },
  { title: "Superstar!", subtitle: "You make getting things done look easy." },
  { title: "Mission Complete!", subtitle: "Another victory in the books." },
  { title: "Way To Go!", subtitle: "Success suits you well." },
];

// localStorage key for sound preference
const SOUND_PREF_KEY = 'celebrationSoundEnabled';

const TaskCompletionCelebration = ({ 
  isOpen, 
  onClose, 
  taskName,
  token
}) => {
  const [message] = useState(() => 
    motivationalMessages[Math.floor(Math.random() * motivationalMessages.length)]
  );
  const [showConfetti, setShowConfetti] = useState(false);
  const [stats, setStats] = useState({
    completed_today: 0,
    completed_this_week: 0,
    streak_days: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem(SOUND_PREF_KEY);
    return saved === null ? true : saved === 'true';
  });
  const audioRef = useRef(null);

  // Toggle sound preference
  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem(SOUND_PREF_KEY, String(newValue));
  };

  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      
      // Play success sound if enabled
      if (soundEnabled) {
        try {
          if (!audioRef.current) {
            audioRef.current = new Audio('/sounds/success-chime.mp3');
            audioRef.current.volume = 0.5;
          }
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(e => {
            console.log('Audio autoplay blocked:', e.message);
          });
        } catch (e) {
          console.log('Audio not supported:', e.message);
        }
      }
      
      // Fetch real stats with a small delay to ensure the task update has been processed
      const fetchStats = async () => {
        setStatsLoading(true);
        // Delay to ensure the task completion is saved before fetching stats
        await new Promise(resolve => setTimeout(resolve, 800));
        try {
          const response = await fetch(`${API}/api/stats/completion`, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Cache-Control': 'no-cache'
            }
          });
          if (response.ok) {
            const data = await response.json();
            setStats(data);
          }
        } catch (error) {
          console.error('Error fetching stats:', error);
        } finally {
          setStatsLoading(false);
        }
      };
      fetchStats();
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, token, soundEnabled]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md w-[95vw] sm:w-full mx-auto p-0 overflow-hidden border-0 bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800" data-testid="celebration-modal">
        {/* Confetti Animation Background */}
        {showConfetti && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(15)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-bounce"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 0.5}s`,
                  animationDuration: `${0.5 + Math.random() * 0.5}s`
                }}
              >
                <Sparkles className={`w-3 h-3 sm:w-4 sm:h-4 ${['text-yellow-400', 'text-orange-400', 'text-green-400', 'text-blue-400', 'text-pink-400'][i % 5]}`} />
              </div>
            ))}
          </div>
        )}

        {/* Header with Trophy */}
        <div className="relative pt-6 sm:pt-8 pb-3 sm:pb-4 text-center bg-gradient-to-b from-orange-500/20 to-transparent">
          <div className="relative inline-block">
            <div className="absolute inset-0 animate-ping bg-orange-500/30 rounded-full scale-150" />
            <div className="relative bg-gradient-to-br from-orange-400 to-amber-500 p-3 sm:p-4 rounded-full shadow-lg shadow-orange-500/30">
              <Trophy className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-4 sm:px-6 pb-4 sm:pb-6 text-center">
          {/* Motivational Message */}
          <h2 className="text-lg sm:text-2xl font-bold text-white mb-1 flex items-center justify-center gap-1 sm:gap-2">
            <PartyPopper className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400 flex-shrink-0" />
            <span className="truncate">{message.title}</span>
            <PartyPopper className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400 scale-x-[-1] flex-shrink-0" />
          </h2>
          <p className="text-xs sm:text-sm text-zinc-400 mb-2">{message.subtitle}</p>
          
          {/* Completed Task Name */}
          <div className="bg-zinc-800/50 rounded-lg p-2 sm:p-3 mb-4 sm:mb-6 border border-zinc-700/50">
            <div className="flex items-center justify-center gap-2 text-green-400">
              <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="font-medium text-sm sm:text-base">Task Completed</span>
            </div>
            <p className="text-white font-medium mt-1 text-sm sm:text-base line-clamp-2 break-words" title={taskName}>
              {taskName || "Task"}
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-6">
            <div className="bg-zinc-800/50 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-zinc-700/50">
              <div className="flex items-center justify-center mb-1">
                <Target className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-white">
                {statsLoading ? '...' : stats.completed_today}
              </p>
              <p className="text-[10px] sm:text-xs text-zinc-500">Today</p>
            </div>
            <div className="bg-zinc-800/50 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-zinc-700/50">
              <div className="flex items-center justify-center mb-1">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-white">
                {statsLoading ? '...' : stats.completed_this_week}
              </p>
              <p className="text-[10px] sm:text-xs text-zinc-500">This Week</p>
            </div>
            <div className="bg-zinc-800/50 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-zinc-700/50">
              <div className="flex items-center justify-center mb-1">
                <Flame className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-white">
                {statsLoading ? '...' : stats.streak_days}
              </p>
              <p className="text-[10px] sm:text-xs text-zinc-500">Day Streak</p>
            </div>
          </div>

          {/* Encouragement */}
          <div className="flex items-center justify-center gap-1 text-xs sm:text-sm text-zinc-500 mb-3 sm:mb-4">
            <Star className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
            <span className="truncate">Keep going! You're making great progress.</span>
            <Star className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
          </div>

          {/* Action Button */}
          <Button 
            onClick={onClose}
            className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold py-3 sm:py-5 text-sm sm:text-base"
            data-testid="celebration-continue-btn"
          >
            Continue Being Awesome
          </Button>

          {/* Sound Toggle */}
          <button
            onClick={toggleSound}
            className="mt-2 sm:mt-3 flex items-center justify-center gap-2 text-xs text-zinc-500 hover:text-zinc-300 transition-colors w-full"
            data-testid="celebration-sound-toggle"
            title={soundEnabled ? 'Click to mute celebration sounds' : 'Click to enable celebration sounds'}
          >
            {soundEnabled ? (
              <>
                <Volume2 className="w-4 h-4" />
                <span>Sound On</span>
              </>
            ) : (
              <>
                <VolumeX className="w-4 h-4" />
                <span>Sound Off</span>
              </>
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TaskCompletionCelebration;
